import React from "react";
import { withRouter } from "react-router";
import { Container, CssContainer } from "../../components/container";
import { Loader } from "../../components/loaders";
import ServiceFactory from "../../service";
import Auth from "../../functions/authenticate";
import ManageDataTabs from "../../components/manage-data-tabs";
import publishSearch from "../../functions/publish-search";
import publishEvent from "../../functions/publish-event";
import { hasTenantFeature } from "../../functions/tenant-features";
import ProviderConnections from "../../components/provider-connections";
import PubSub from "pubsub-js";
import account from "../../data/account";
import { ImportWizard, SA_EMAIL } from "./ga4-import-wizard";

/*
 * Connect Your Data — the onboarding checklist (ONBOARDING_DATA_IMPORT.md
 * Part B + mockup connect-your-data.html). One card per feed: plain-language
 * purpose, "what turns on" chips, live status from the tenant's
 * data_feed_schedule/run rows, freshness. Copy rule: a missing feed is an
 * invitation, not an error.
 *
 * History Import (Part A, GA4 Tier 1) is CAPABILITY-PROBED: the card's
 * wizard only activates when GET /v1/data-feeds/providers lists
 * `HistoricalImport` — the deployed AnalyticsService rejected it on probe
 * (2026-08-10, "Unknown feed provider") despite the mockup's backend-live
 * note, so until that SAM deploy lands the card says "arriving soon" and
 * the wizard stays dormant. Same probe pattern applies to Inventory (card
 * appears only when its provider ships). The wizard itself follows the
 * recorded contract exactly:
 *   POST /schedules {provider:"HistoricalImport", settings:{Ga4PropertyId,
 *        Ga4ServiceAccountJson, Ga4MonthsBack}}
 *   POST /schedules/{id}/run?mode=preview   (dry-run; match rate in Message)
 *   POST /schedules/{id}/run                (full import)
 *   GET  /runs                              (progress/results)
 */

function svc(api) {
  const Service = ServiceFactory.getType(`/data-feeds${api}`, "product");
  return new Service();
}
function activitySvc(api) {
  const Service = ServiceFactory.getType(api); // 'activity' root
  return new Service();
}
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
export function ago(d) {
  if (!d) return "";
  const ms = Date.now() - new Date(d).getTime();
  if (isNaN(ms)) return "";
  const h = Math.floor(ms / 3600000);
  if (h < 1) return "under an hour ago";
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
export { SA_EMAIL };

// Feed catalog (spec B1). `provider` binds a card to schedule rows;
// `probe: true` means the card only renders when the provider is deployed.
export const CARDS = [
// Shopify workspaces (2026-09-27): the store connection IS the product, sales, customer and
// inventory feed — those cards read "Via Shopify" and the ERP-only cards (reps) hide.
{
  key: "shopify",
  provider: "Shopify",
  shopifyOnly: true,
  required: true,
  name: "Shopify store",
  line: "Products, orders, customers, inventory, segments and markets — one connection, synced daily plus live webhooks.",
  chips: ["Catalog", "Sales", "Customers", "Inventory", "Segments", "Markets"],
  manageRoute: "/Manage/Shopify"
}, {
  key: "product",
  provider: "Product",
  required: true,
  viaShopify: true,
  name: "Product feed",
  line: "Your catalog — powers tracking match, Studio, forecasts and comparables.",
  chips: ["Catalog", "Forecasts", "Studio"],
  contract: "Google Shopping format CSV: id, title, description, link, image_link, price, product_type, availability. The Feeds tab's Validate button checks your file's headers before anything saves."
}, {
  key: "tracking",
  kind: "tracking",
  required: true,
  name: "Tracking snippet",
  line: "Live traffic, contacts and funnels — everything from today forward.",
  chips: ["Contacts", "Funnels", "Live interest"],
  contract: "One script tag on every page of your site. Your Plunk contact installs it with your web team — it's not a feed, but it's the other half of day one."
}, {
  key: "sales",
  provider: "SalesTransaction",
  viaShopify: true,
  name: "Sales transactions",
  line: "Orders from your ERP — units sold, revenue, sales forecasts.",
  chips: ["Top Sellers", "Sales forecasts", "Price Opportunity"],
  feature: "salesTransactions",
  contract: "CSV per order line: order id, date, product id/SKU, quantity, price (the EXTENDED line total — unit × quantity, not unit price), customer number, status.",
  recommended: [["Cover / fabric selection", "The fabric, finish, or leather chosen for each upholstered line. Send it either as a column on the frame line, or as separate fabric lines that carry a reference linking them to the frame's order (project no., parent order, quote no.). Unlocks Configuration reporting — which fabrics and finishes each of your styles actually sells in."], ["Order link reference", "If fabrics or options invoice on separate orders, include the field that ties them to the parent order."]]
}, {
  key: "customers",
  provider: "Customer",
  viaShopify: true,
  shopifyName: "Customers",
  name: "Customers / dealers",
  line: "Your account list — unifies sales, contacts and lifecycle stages per customer.",
  chips: ["Customer unification", "Lifecycle stages", "Rep target lists"],
  contract: "CSV per account: customer number, name, email, phone, address, region, assigned rep. Any subset works; the number is the join key to your sales feed."
}, {
  key: "reps",
  provider: "SalesRep",
  hideForShopify: true,
  name: "Sales reps + territories",
  line: "Your rep roster and territory map — rep intelligence and coverage.",
  chips: ["Territory intelligence", "Rep leaderboards"],
  feature: "territoryRep",
  contract: "CSV per rep: rep id (must match the rep ids your sales feed carries), name, email, territory/states covered, status."
}, {
  key: "audience",
  provider: "Audience",
  optional: true,
  name: "Audience feed",
  line: "Seed an audience from an external list — memberships add on each run.",
  chips: ["Audience seeding"],
  contract: "CSV per member: email (join key), name, phone. Target audience is picked when the feed is set up — never free-text."
},
// §61 checklist tasks (UX note 2026-08-17) — not feeds; excluded from the
// "N of M connected" count. Ordering per the note: feeds → tracking →
// markets → lifecycle → configuration reporting → history import.
{
  key: "markets",
  kind: "task",
  route: "/Locations",
  name: "Define your markets",
  line: "Markets power the geographic views, audience scoping and Market Intelligence.",
  chips: ["Geo views", "Audience scoping", "MI"],
  cta: "Open Markets",
  // §284 — steps the assistant can EXECUTE carry an ask-Plunk affordance
  askStarter: "Help me set up my target markets"
}, {
  key: "lifecycleFields",
  kind: "task",
  route: "/Lifecycle/this_month",
  name: "Map your lifecycle fields",
  line: "Tell the lifecycle engine which payload fields carry name, email and phone — the suggest flow drafts the mapping from your observed data.",
  chips: ["Lifecycle stages", "Contact identity"],
  cta: "Open Lifecycle settings",
  askStarter: "Help me map my lifecycle fields"
}, {
  key: "configReporting",
  kind: "task",
  route: "/Data/Rules",
  needsSales: true,
  name: "Configuration reporting",
  line: "Which fabrics and finishes each of your styles actually sells in — parsed from your sales lines by rules you control.",
  chips: ["Styles", "Fabrics", "Finishes"],
  cta: "Open Configuration Rules"
}, {
  key: "history",
  provider: "HistoricalImport",
  probe: true,
  oneTime: true,
  name: "Google Analytics import",
  line: "Bring in up to 24 months of history from before your tracking started, or fill a stretch your tracking missed.",
  chips: ["Day-one forecasts", "Seasonality", "Gap fills"]
}, {
  key: "inventory",
  provider: "Inventory",
  probe: true,
  hideWhenAbsent: true,
  viaShopify: true,
  name: "Inventory",
  line: "Stock and in-transit — availability chips and restock forecasts.",
  chips: ["Stock chips", "Restock watch"]
}];

// History import + tracking-gap fill wizard lives in ./ga4-import-wizard (2026-10-14).
export { ImportWizard };

// ── §286 — "Seed your apps" (tune-tail): six run-a-tool apps start
//    empty; Ask Plunk suggests starting content, the user confirms in
//    chat, the tools seed. Row states read the SAME endpoints the Apps
//    cards use (views/apps/index.jsx meta loaders — kept in sync by
//    hand; both fail soft). Free config seeds immediately; credit-
//    spending runs (Company Analysis / Market Trends) confirm per run. ──
export const SEED_APPS = [{
  key: "keywords",
  icon: "manage_search",
  name: "Keyword Intelligence",
  means: "keywords + competitors",
  starter: "Suggest keywords and competitor domains to track for my business",
  load: async () => {
    const rows = await safeCall(seedSvc("/keywords/overview", "research"), []);
    return rows.length ? `${rows.length} keyword${rows.length === 1 ? "" : "s"} tracked` : null;
  }
}, {
  key: "aivis",
  icon: "psychology",
  name: "AI Visibility",
  means: "prompts to track",
  starter: "Suggest AI prompts to track for my brand",
  load: async () => {
    const rows = await safeCall(seedSvc("/keywords/ai/prompts", "research"), []);
    return rows.length ? `${rows.length} prompt${rows.length === 1 ? "" : "s"} tracked` : null;
  }
}, {
  key: "seo",
  icon: "travel_explore",
  name: "SEO Analysis",
  means: "site + first crawl",
  starter: "Set up an SEO health crawl for my website",
  load: async () => {
    const sites = (await safeCall(seedSvc("/seo/sites", "product"), [])).filter(s => s.status !== "Inactive");
    return sites.length ? `${sites.length} site${sites.length === 1 ? "" : "s"} monitored` : null;
  }
}, {
  key: "ada",
  icon: "accessibility_new",
  name: "ADA Monitor",
  means: "site + first crawl",
  starter: "Set up an accessibility (ADA) scan for my website",
  load: async () => {
    const sites = (await safeCall(seedSvc("/ada/sites", "product"), [])).filter(s => s.status !== "Inactive");
    return sites.length ? `${sites.length} site${sites.length === 1 ? "" : "s"} monitored` : null;
  }
}, {
  key: "company",
  icon: "data_exploration",
  name: "Company Analysis",
  means: "report per competitor",
  starter: "Run a company analysis on one of my competitors",
  load: async () => {
    const rows = await safeCall(seedSvc("/company-analysis?$top=1&$orderByDesc=created_date", "research"), []);
    const r = Array.isArray(rows) ? rows[0] : null;
    return r ? `last report ${r.createdDate ? ago(r.createdDate) : "—"}` : null;
  }
}, {
  key: "trends",
  icon: "query_stats",
  name: "Market Trends",
  means: "first category report",
  starter: "Run a market trends report for my top product category",
  load: async () => {
    const rows = await safeCall(seedSvc("/market-trends?$top=1&$orderByDesc=created_date", "research"), []);
    const r = Array.isArray(rows) ? rows[0] : null;
    return r ? `last report ${r.createdDate ? ago(r.createdDate) : "—"}` : null;
  }
}];
function seedSvc(api, ep) {
  const Service = ServiceFactory.getType(api, ep);
  return new Service().getAsync();
}
async function safeCall(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
export const askPlunk = starter => PubSub.publish("plunk-assistant-toggle", {
  starter
});
class SeedAppsCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      states: {}
    }; // key → string (seeded) | null (empty)
    this._alive = true;
  }
  componentDidMount() {
    SEED_APPS.forEach(a => a.load().then(v => {
      if (this._alive) this.setState(st => ({
        states: {
          ...st.states,
          [a.key]: v
        }
      }));
    }).catch(() => {}));
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    const {
      states
    } = this.state;
    const badge = {
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: ".05em",
      border: "1px solid var(--plk-border, #dcdcdc)",
      borderRadius: "6px",
      padding: "2px 8px",
      color: "var(--plk-text-dim, #737373)"
    };
    return /*#__PURE__*/React.createElement(Container, {
      css: "margin-squeeze cyd-card"
    }, /*#__PURE__*/React.createElement("h3", {
      style: {
        margin: "0 0 4px"
      }
    }, "Seed your apps"), /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 10px",
        fontSize: "13px"
      }
    }, "Your six apps start empty \u2014 Ask Plunk suggests starting content from your business, you approve, it seeds. Free config lands immediately; report runs that spend credits are confirmed one by one."), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "5px 18px",
        marginBottom: "10px"
      }
    }, SEED_APPS.map(a => {
      const seeded = states[a.key];
      return /*#__PURE__*/React.createElement("span", {
        key: a.key,
        style: {
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "12.5px"
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons",
        style: {
          fontSize: "15px",
          color: "var(--plk-text-dim)"
        }
      }, a.icon), /*#__PURE__*/React.createElement("span", {
        style: {
          minWidth: 0
        }
      }, a.name, " \u2014 ", /*#__PURE__*/React.createElement("i", {
        style: {
          color: "var(--plk-text-dim)"
        }
      }, a.means), " ", seeded ? /*#__PURE__*/React.createElement("b", {
        style: {
          color: "#217a4b"
        }
      }, "\xB7 ", seeded) : /*#__PURE__*/React.createElement("b", {
        style: {
          color: "var(--plk-text-dim)"
        }
      }, "\xB7 Empty")), !seeded && /*#__PURE__*/React.createElement("a", {
        style: {
          cursor: "pointer",
          fontSize: "11.5px",
          whiteSpace: "nowrap",
          marginLeft: "auto"
        },
        title: `Ask Plunk: ${a.starter}`,
        onClick: () => askPlunk(a.starter)
      }, "\uD83D\uDCAC seed"));
    })), /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 10px",
        display: "flex",
        gap: "6px",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: badge
    }, "SUGGEST \u2192 YOU CONFIRM \u2192 SEEDED"), /*#__PURE__*/React.createElement("span", {
      style: badge
    }, "RUNS THAT SPEND CREDITS ASK FIRST")), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "8px"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => askPlunk("Help me seed my apps")
    }, "\uD83D\uDCAC Ask Plunk to seed"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      onClick: () => this.props.history.push("/Apps")
    }, "Open Apps")));
  }
}
class ConnectDataComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      providers: null,
      schedules: null,
      runs: [],
      trackingLive: null,
      wizard: false,
      contractOpen: null,
      pointerDismissed: (() => {
        try {
          return localStorage.getItem("plunk-onboarding-askplunk-pointer") === "1";
        } catch (e) {
          return false;
        }
      })()
    };
    this._alive = true;
  }
  async componentDidMount() {
    publishSearch("Connect Your Data");
    publishEvent("reset-search", {});
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    const rulesSvc = api => {
      const Service = ServiceFactory.getType(api, "product");
      return new Service();
    };
    const [providers, schedules, runs, dash, locations, lifecycleRules, configRules] = await Promise.all([safe(svc("/providers").getAsync(), []), safe(svc("/schedules").getAsync(), []), safe(svc("/runs?top=50").getAsync(), []), safe(activitySvc("/dashboard/this_month").getAsync(), null), safe(rulesSvc("/location").getAsync(), null), safe(rulesSvc("/lifecycle/config").getAsync(), null), safe(rulesSvc("/product/configurations/rules").getAsync(), null)]);
    if (!this._alive) return;
    // Tracking-live probe: any nonzero count in the dashboard payload.
    let trackingLive = null;
    if (dash) {
      const nums = [];
      const walk = (o, depth) => {
        if (depth > 3 || o == null) return;
        if (typeof o === "number") {
          nums.push(o);
          return;
        }
        if (Array.isArray(o)) {
          o.slice(0, 50).forEach(x => walk(x, depth + 1));
          return;
        }
        if (typeof o === "object") Object.keys(o).forEach(k => walk(o[k], depth + 1));
      };
      walk(dash, 0);
      trackingLive = nums.some(n => n > 0);
    }
    // §61 task-card state
    const marketCount = locations ? locations.items && locations.items.length || (Array.isArray(locations) ? locations.length : 0) : null;
    let lifecycleFieldCount = null;
    if (Array.isArray(lifecycleRules)) {
      const keys = new Set();
      lifecycleRules.forEach(r => {
        if (!r || r.active === false || !r.fieldMap) return;
        try {
          Object.keys(JSON.parse(r.fieldMap)).forEach(k => keys.add(k));
        } catch (e) {/* bad json */}
      });
      lifecycleFieldCount = {
        rules: lifecycleRules.length,
        fields: keys.size
      };
    }
    // A grammar can predate the versioned PUT (seeded directly → version 0
    // with rules present) — key "set up" on rules existing, not the version.
    const configHasRules = !!(configRules && configRules.rules);
    const configVersion = configRules ? configRules.version || 0 : null;
    this.setState({
      providers: providers || [],
      schedules: schedules || [],
      runs: runs || [],
      trackingLive,
      marketCount,
      lifecycleFieldCount,
      configVersion,
      configHasRules
    });
    // Configuration-reporting live state: run the coverage preview lazily
    // once rules exist — its styles/pairs + unpairedReason are exactly the
    // onboarding-time signal (§61: "the card would have surfaced Ambella's
    // whole story on day one"). Non-blocking; the card upgrades in place.
    if (configHasRules) {
      safe(rulesSvc("/product/configurations/rules/preview").postAsync({}), null).then(rep => {
        if (this._alive && rep && !rep.empty) this.setState({
          configPreview: rep
        });
      });
    }
  }
  componentWillUnmount() {
    this._alive = false;
  }
  hasProvider(name) {
    return (this.state.providers || []).some(p => p.name === name);
  }
  scheduleFor(provider) {
    return (this.state.schedules || []).find(s => s.provider === provider) || null;
  }
  lastRunFor(schedule) {
    if (!schedule) return null;
    return (this.state.runs || []).find(r => String(r.scheduleId) === String(schedule.id)) || null;
  }
  cardStatus(card) {
    if (card.kind === "task") {
      const {
        marketCount,
        lifecycleFieldCount,
        configVersion,
        configPreview
      } = this.state;
      if (card.key === "markets") {
        if (marketCount === null || marketCount === undefined) return {
          label: "—",
          cls: "off"
        };
        return marketCount > 0 ? {
          label: `${marketCount} market${marketCount === 1 ? "" : "s"} defined`,
          cls: "ok"
        } : {
          label: "Not defined",
          cls: "off"
        };
      }
      if (card.key === "lifecycleFields") {
        if (!lifecycleFieldCount) return {
          label: "Not set up",
          cls: "off"
        };
        if (lifecycleFieldCount.fields > 0) return {
          label: `${lifecycleFieldCount.fields} fields mapped`,
          cls: "ok"
        };
        if (lifecycleFieldCount.rules > 0) return {
          label: "Rules active — fields not mapped",
          cls: "info"
        };
        return {
          label: "Not set up",
          cls: "off"
        };
      }
      if (card.key === "configReporting") {
        if (!this.state.configHasRules) return {
          label: "Not set up",
          cls: "off"
        };
        if (configPreview) return {
          label: `Reporting live · ${(configPreview.styles || 0).toLocaleString()} styles · ${(configPreview.pairs || 0).toLocaleString()} pairs`,
          cls: "ok"
        };
        return {
          label: configVersion > 0 ? `Rules saved (v${configVersion})` : "Rules saved",
          cls: "info"
        };
      }
      return {
        label: "—",
        cls: "off"
      };
    }
    if (card.kind === "tracking") {
      const {
        trackingLive
      } = this.state;
      if (trackingLive === true) return {
        label: "Live",
        cls: "ok"
      };
      if (trackingLive === false) return {
        label: "No traffic yet",
        cls: "off"
      };
      return {
        label: "—",
        cls: "off"
      };
    }
    // Shopify covers this card: no feed of its own, but the store connection delivers it.
    if (card.viaShopify && !this.scheduleFor(card.provider) && this.scheduleFor("Shopify")) {
      const st = this.cardStatus({
        key: "shopify",
        provider: "Shopify"
      });
      return {
        ...st,
        label: st.cls === "ok" ? "Via Shopify" + st.label.replace(/^Connected/, "") : st.label,
        via: true
      };
    }
    if (card.probe && !this.hasProvider(card.provider)) return {
      label: "Arriving soon",
      cls: "info"
    };
    const sched = this.scheduleFor(card.provider);
    if (!sched) return {
      label: "Not connected",
      cls: "off"
    };
    if (sched.status !== "Active") return {
      label: "Paused",
      cls: "off"
    };
    const run = this.lastRunFor(sched);
    if (run && run.finishedDate && run.success === false) return {
      label: "Error — last run failed",
      cls: "warn",
      run
    };
    if (run && run.finishedDate) return {
      label: `Connected · ${String(sched.frequency || "").toLowerCase()} · ${ago(run.finishedDate)}`,
      cls: "ok",
      run
    };
    return {
      label: `Connected · ${String(sched.frequency || "").toLowerCase()}`,
      cls: "ok"
    };
  }
  render() {
    const {
      providers,
      wizard,
      contractOpen
    } = this.state;
    if (providers === null) return /*#__PURE__*/React.createElement("div", {
      className: "territory-view"
    }, /*#__PURE__*/React.createElement(ManageDataTabs, {
      active: "/Data/Connect"
    }), /*#__PURE__*/React.createElement(Loader, null));
    const shopify = !!this.scheduleFor("Shopify");
    const cards = CARDS.filter(c => !(c.hideWhenAbsent && !this.hasProvider(c.provider) && !(shopify && c.viaShopify))).filter(c => !(c.needsSales && !this.scheduleFor("SalesTransaction") && !shopify)).filter(c => shopify ? !(c.hideForShopify && !this.scheduleFor(c.provider)) : !c.shopifyOnly);
    const statuses = {};
    cards.forEach(c => {
      statuses[c.key] = this.cardStatus(c);
    });
    // Cards the store connection covers count once, as the store.
    const countable = cards.filter(c => !c.oneTime && !c.optional && c.kind !== "task" && !(statuses[c.key] && statuses[c.key].via));
    const connected = countable.filter(c => statuses[c.key].cls === "ok" || c.kind === "tracking" && statuses[c.key].label === "Live").length;
    const nextUp = countable.find(c => statuses[c.key].cls === "off" && c.kind !== "tracking");
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view cyd-view"
    }, /*#__PURE__*/React.createElement(ManageDataTabs, {
      active: "/Data/Connect"
    }), wizard && /*#__PURE__*/React.createElement(ImportWizard, {
      initialMode: wizard === "gap" ? "gap" : wizard === "history" ? "history" : null,
      onClose: () => this.setState({
        wizard: false
      })
    }), /*#__PURE__*/React.createElement(CssContainer, {
      cls: "grid-button space-between margin-squeeze align-end"
    }, /*#__PURE__*/React.createElement("div", {
      className: "heading-label"
    }, "Connect Your Data")), /*#__PURE__*/React.createElement(Container, {
      css: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 6px"
      }
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        fontSize: "15px"
      }
    }, connected, " of ", countable.length, " connected"), nextUp && /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        marginLeft: "10px"
      }
    }, "\u2014 your ", nextUp.name.toLowerCase(), " unlocks ", nextUp.chips.slice(0, 2).join(" and "), ".")), /*#__PURE__*/React.createElement("div", {
      className: "cyd-progress"
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${Math.round(connected / Math.max(1, countable.length) * 100)}%`
      }
    }))), /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "12px",
        alignItems: "center",
        flexWrap: "wrap",
        border: "1px dashed var(--plk-border, #dcdcdc)",
        borderRadius: "11px",
        padding: "11px 16px",
        fontSize: "12.5px"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        flex: "1 1 420px"
      }
    }, /*#__PURE__*/React.createElement("b", null, "Need a hand?"), " Ask Plunk can DO most of this setup with you \u2014 generate your events script, add target markets, draft your follow-up emails, build your first report, seed all six apps \u2014 or request a walkthrough and a person will do it with you."), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => askPlunk("Help me finish setting up my workspace")
    }, "\uD83D\uDCAC Ask Plunk"), /*#__PURE__*/React.createElement("a", {
      className: "territory-toggle",
      style: {
        textDecoration: "none"
      },
      href: `mailto:support@plunk.io?subject=${encodeURIComponent("Onboarding walkthrough request")}&body=${encodeURIComponent("We'd like a guided walkthrough of workspace setup.\n\nWorkspace: " + ((account.get() || {}).tenantId || ""))}`
    }, "Request a walkthrough"))), /*#__PURE__*/React.createElement("div", {
      className: "cyd-cards"
    }, cards.map(card => {
      const st = statuses[card.key];
      const soon = st.label === "Arriving soon";
      const open = contractOpen === card.key;
      return /*#__PURE__*/React.createElement(Container, {
        key: card.key,
        css: "margin-squeeze cyd-card"
      }, /*#__PURE__*/React.createElement("h3", {
        style: {
          margin: "0 0 4px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap"
        }
      }, this.scheduleFor("Shopify") && card.shopifyName ? card.shopifyName : card.name, /*#__PURE__*/React.createElement("span", {
        className: `simf-pill ${st.cls === "off" ? "info" : st.cls}`,
        style: st.cls === "off" ? {
          background: "#eef0f3",
          color: "#8a94a6"
        } : undefined
      }, st.label), card.required && st.cls === "off" && /*#__PURE__*/React.createElement("span", {
        className: "simf-pill warn"
      }, "Start here")), /*#__PURE__*/React.createElement("p", {
        style: {
          margin: "0 0 8px",
          fontSize: "13px"
        }
      }, card.line), /*#__PURE__*/React.createElement("p", {
        style: {
          margin: "0 0 10px"
        }
      }, card.chips.map(c => /*#__PURE__*/React.createElement("span", {
        key: c,
        className: "cyd-chip"
      }, c)), card.feature && hasTenantFeature(card.feature) && /*#__PURE__*/React.createElement("span", {
        className: "cyd-chip on"
      }, "enabled")), st.run && st.cls === "warn" && /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          color: "#a06a00",
          margin: "0 0 8px"
        },
        title: st.run.message || ""
      }, String(st.run.message || "The last run failed.").slice(0, 140)), card.key === "configReporting" && this.state.configPreview && this.state.configPreview.unpairedReason && /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          borderLeft: "3px solid #ffd246",
          paddingLeft: "10px",
          margin: "0 0 8px"
        }
      }, this.state.configPreview.unpairedReason), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: "8px",
          flexWrap: "wrap"
        }
      }, card.key === "history" && !soon && /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
        onClick: () => this.setState({
          wizard: "history"
        })
      }, "Import history"), card.key === "history" && !soon && /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.setState({
          wizard: "gap"
        })
      }, "Fill a tracking gap"), card.key === "history" && soon && /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, "Google Analytics import: the import service is rolling out; this card activates itself when it's ready."), card.kind === "tracking" && /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
        onClick: () => this.props.history.push("/Tracking")
      }, st.label === "Live" ? "View tracking" : "Set up tracking"), (card.manageRoute || st.via) && /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.props.history.push(card.manageRoute || "/Manage/Shopify")
      }, "Manage store"), card.provider && !card.probe && !card.manageRoute && /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.props.history.push("/Data")
      }, st.cls === "off" ? "Connect" : st.via ? "Add a separate feed" : "Manage feed"), card.kind === "task" && /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.props.history.push(card.route)
      }, card.cta), card.askStarter && /*#__PURE__*/React.createElement("a", {
        className: "territory-hint",
        style: {
          margin: 0,
          alignSelf: "center",
          cursor: "pointer"
        },
        title: `Ask Plunk: ${card.askStarter}`,
        onClick: () => askPlunk(card.askStarter)
      }, "or ask Plunk"), card.contract && /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.setState({
          contractOpen: open ? null : card.key
        })
      }, open ? "Hide contract" : "View contract")), open && /*#__PURE__*/React.createElement("div", {
        style: {
          margin: "10px 0 0",
          maxWidth: "70ch"
        }
      }, /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, card.contract), (card.recommended || []).length > 0 && /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: "8px"
        }
      }, /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: "0 0 4px",
          fontWeight: 600
        }
      }, "Recommended"), card.recommended.map(([t, body]) => /*#__PURE__*/React.createElement("p", {
        key: t,
        className: "territory-hint",
        style: {
          margin: "0 0 6px"
        }
      }, /*#__PURE__*/React.createElement("b", null, t), " \u2014 ", body)))));
    })), /*#__PURE__*/React.createElement(SeedAppsCard, {
      history: this.props.history
    }), !this.state.pointerDismissed && /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: 0,
        display: "flex",
        gap: "10px",
        alignItems: "flex-start"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        flex: 1
      }
    }, "Meet ", /*#__PURE__*/React.createElement("b", null, "Ask Plunk"), " \u2014 the chat button answers questions about your own data from day one, and it can run setup itself: it checks what's done, then generates the events script, creates markets, drafts email automations and reports, and seeds your apps \u2014 everything lands as a draft or config for you to approve. Connecting accounts and uploading files stay yours."), /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      title: "Dismiss",
      onClick: () => {
        try {
          localStorage.setItem("plunk-onboarding-askplunk-pointer", "1");
        } catch (e) {/* private */}
        this.setState({
          pointerDismissed: true
        });
      }
    }, "\u2715"))), /*#__PURE__*/React.createElement(ProviderConnections, {
      history: this.props.history
    }));
  }
}
const Routable = withRouter(ConnectDataComponent);
export class ConnectDataViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, {
      params: this.props.params
    });
  }
}
export default {
  ConnectDataViewComponent
};